// components/ConfirmDeleteModal.tsx

import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { getModalStyles, getPrimaryText, getButtonStyles } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

type ConfirmDeleteModalProps = {
  isOpen: boolean;
  title?: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  /** Overrides the confirmText-based button styling when set */
  confirmVariant?: "primary" | "danger";
  /** Disables the confirm button (e.g. a required input is still empty) */
  confirmDisabled?: boolean;
  /** Rendered between the message and the buttons (e.g. a reason input) */
  children?: React.ReactNode;
};

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  title = "Confirm Deletion",
  message,
  onCancel,
  onConfirm,
  confirmText = "Delete",
  cancelText = "Cancel",
  loading = false,
  confirmVariant,
  confirmDisabled = false,
  children,
}) => {
  const { isDarkMode } = useTheme();

  if (!isOpen) return null;

  const buttonStyles = getButtonStyles(isDarkMode);

  return (
    <div className={`fixed inset-0 flex items-center justify-center ${TOKENS.overlay.backdrop} z-50`}>
      <div className={`${getModalStyles(isDarkMode)} rounded-lg p-6 shadow-lg w-[90%] max-w-md border`}>
        <h3 className={`text-lg font-semibold mb-4 ${getPrimaryText(isDarkMode)}`}>{title}</h3>
        <p className={`mb-6 whitespace-pre-line ${getPrimaryText(isDarkMode)}`}>{message}</p>
        {children}
        <div className="flex justify-end gap-4">
          <button
            className={`px-4 py-2 rounded ${buttonStyles.secondary} disabled:opacity-50 transition-colors`}
            onClick={onCancel}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={`px-4 py-2 rounded ${(confirmVariant ? confirmVariant==="primary" : confirmText==="Enroll" || confirmText==="Reactivate" || confirmText==="Update Email" || confirmText==="Approve")?buttonStyles.primary:buttonStyles.danger} disabled:opacity-50 transition-colors`}
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
          >
            {loading ? (
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteModal;
